import { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

function getHiddenTransform(direction, distance) {
  switch (direction) {
    case 'left':
      return `translate3d(${distance}px, 0, 0)`;
    case 'right':
      return `translate3d(-${distance}px, 0, 0)`;
    case 'down':
      return `translate3d(0, -${distance}px, 0)`;
    case 'up':
    default:
      return `translate3d(0, ${distance}px, 0)`;
  }
}

const RevealWrap = styled.div`
  opacity: ${({ $visible }) => ($visible ? 1 : 0)};
  transform: ${({ $visible, $direction, $distance }) =>
    $visible ? 'translate3d(0, 0, 0)' : getHiddenTransform($direction, $distance)};
  filter: ${({ $visible }) => ($visible ? 'blur(0)' : 'blur(8px)')};

  transition:
    opacity ${({ $duration }) => $duration}ms ease,
    transform ${({ $duration }) => $duration}ms cubic-bezier(0.22, 1, 0.36, 1),
    filter ${({ $duration }) => $duration}ms ease;

  transition-delay: ${({ $delay }) => $delay}ms;
  will-change: opacity, transform, filter;

  @media (prefers-reduced-motion: reduce) {
    opacity: 1;
    transform: none;
    filter: none;
    transition: none;
  }
`;

function Reveal({
  children,
  delay = 0,
  duration = 850,
  direction = 'up',
  distance = 34,
  threshold = 0.18,
}) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const element = ref.current;

    if (!element) return;

    if (!('IntersectionObserver' in window)) {
      setVisible(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.unobserve(element);
        }
      },
      {
        threshold,
        rootMargin: '0px 0px -8% 0px',
      }
    );

    observer.observe(element);

    return () => observer.disconnect();
  }, [threshold]);

  return (
    <RevealWrap
      ref={ref}
      $visible={visible}
      $delay={delay}
      $duration={duration}
      $direction={direction}
      $distance={distance}
    >
      {children}
    </RevealWrap>
  );
}

export default Reveal;